import { Chip, Image, Text } from '@msc-b2c/shared-components';
import styles from './imagetitle.module.scss';
import {
  defaultPillsVisibility,
  getActBaseImagePath,
  getActiveSession,
  getActPlaceholderImagePath,
  getObsBaseImagePath,
  getObsPlaceholderImagePath,
  RootState,
  useAssetAltText,
  useGetBookingQuery,
} from '@msc-b2c/shared-utils';
import { skipToken } from '@reduxjs/toolkit/query';
import { useSelector } from 'react-redux';
import { ImageTitleProps } from '@msc-b2c/interfaces/interfaces';
import HeadsUpContainer from './HeadsUpContainer';
import { filterPills } from '../../../utils/pillManagementUtils';
import { PageType } from '@msc-b2c/interfaces/enums';

const ImageTitle = ({
  postBookingWrapperID = '',
  imageCode,
  booking,
  pageType = PageType.OTHER,
  useOverlayTitleSubtitle = false,
}: ImageTitleProps) => {
  const urlCountry = getActBaseImagePath() || '/content/dam/msc-cruises/b2c-assets/act';

  const selectedExcursionCard = useSelector(
    (state: RootState) => state.myAreaDrawers.selectedMyAreaExcursionCard
  );
  const selectedMyAreaOBSCard = useSelector(
    (state: RootState) => state.myAreaDrawers.selectedMyAreaOBSCard
  );
  const isLayerMyAreaExcursionOpen = useSelector(
    (state: RootState) => state.myAreaDrawers.isLayerMyAreaExcursionOpen
  );
  const excursionTitle = useSelector(
    (state: RootState) => state.myAreaDrawers.selectedMyAreaExcursionCard?.description
  );
  const obsTitle = useSelector(
    (state: RootState) => state.myAreaDrawers.selectedMyAreaOBSCard?.description
  );
  const drawerPillsConfig = useSelector(
    (state: RootState) => state.myAreaCommonConfig.drawerProps.drawerPillConfigurations
  );
  const categoryOrder = useSelector((state: RootState) => state.myAreaCommonConfig.categoryOrder);
  const relatedObsTitlesMap = useSelector(
    (state: RootState) => state.myAreaDrawers.relatedObsTitlesMap
  );
  const myAreaPillsVisibilityConfig = useSelector(
    (state: RootState) => state.myAreaCommonConfig.pillsVisibility[postBookingWrapperID]
  );
  const cartItems = useSelector((state: RootState) => state.myAreaCart.items);
  const myAreaPillsVisibility = myAreaPillsVisibilityConfig ?? defaultPillsVisibility;

  const prepaidCardType = useSelector((state: RootState) => state.myAreaCommonConfig.prepaidType);
  const bookingNumber = getActiveSession().bookingNumber ?? '';
  const { data: bookingData } = useGetBookingQuery(bookingNumber || skipToken, {
    selectFromResult: ({ data }) => ({ data }),
  });

  const relatedTitle = relatedObsTitlesMap?.[imageCode] ?? null;

  const mainTitle = isLayerMyAreaExcursionOpen ? excursionTitle ?? '' : obsTitle ?? '';
  const subtitle = Array.isArray(relatedTitle) ? relatedTitle.join(', ') : '';
  const fullTitle = subtitle ? `${mainTitle}, ${subtitle}` : mainTitle;

  const localizedAssetAlt = useAssetAltText(imageCode, isLayerMyAreaExcursionOpen ? 'act' : 'obs');

  const image = isLayerMyAreaExcursionOpen
    ? `${urlCountry}/${imageCode}.jpg`
    : `${getObsBaseImagePath()}/${imageCode}.jpg`;

  const pillsToShow = isLayerMyAreaExcursionOpen
    ? selectedExcursionCard
      ? filterPills(
          selectedExcursionCard,
          drawerPillsConfig,
          myAreaPillsVisibility,
          categoryOrder,
          booking,
          cartItems,
          pageType
        )
      : null
    : selectedMyAreaOBSCard
    ? filterPills(
        selectedMyAreaOBSCard,
        drawerPillsConfig,
        myAreaPillsVisibility,
        categoryOrder,
        booking,
        cartItems,
        pageType
      )
    : null;

  return (
    <>
      <div className={styles.headerImage}>
        <div className={styles.pillsBar}>
          {pillsToShow &&
            pillsToShow.length > 0 &&
            pillsToShow.map((pill, index) => (
              <Chip
                key={`${index}-${pill.pillLabel}`}
                icon={pill.pillIcon ?? ''}
                text={pill.pillLabel}
                ribbonBGColor={pill.backgroundColor}
                ribbonTextColor={pill.fontColor}
                isBadge
                styleOption={styles.pill}
                isIconOnTheLeft={!!pill.iconOnTheLeft}
              />
            ))}
        </div>
        <Image
          src={image}
          alt={
            localizedAssetAlt ||
            (isLayerMyAreaExcursionOpen ? excursionTitle ?? '' : obsTitle ?? '')
          }
          placeholderImagePath={
            isLayerMyAreaExcursionOpen ? getActPlaceholderImagePath() : getObsPlaceholderImagePath()
          }
        />
        {useOverlayTitleSubtitle && (
          <div className={styles.headerContent}>
            <div className={styles.headerTitle}>
              <Text text={mainTitle} />
            </div>
            {subtitle && (
              <div className={styles.headerSubtitle}>
                <Text text={subtitle} />
              </div>
            )}
          </div>
        )}
      </div>

      {prepaidCardType === 'Excursions' && <HeadsUpContainer bookingData={bookingData} />}

      {!useOverlayTitleSubtitle && (
        <div className={styles.excursionTitle}>
          <Text text={fullTitle} />
        </div>
      )}
    </>
  );
};

export default ImageTitle;
